import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { ApiError, ApiUnreachableError } from "../api/client.js";
import type { DownloadedFile, PlanPaperSize } from "../api/types.js";
import { usePortal } from "../app/context.js";
import { useSubmit } from "../app/use-submit.js";
import { HowToLink } from "../help/HowToLink.jsx";

/**
 * "Export" — the survey's plan as files, and its room schedule.
 *
 * Every item fetches through the authenticated client (a plain `<a href>`
 * cannot send the Bearer header), gets the bytes back as an object URL, clicks
 * a temporary download link and lets the URL go a minute later — the way the
 * installer pack's PDF downloads. Nothing is stored server-side: each file is
 * drawn from the live plan when it is asked for.
 *
 * The storey picker appears only when there is more than one storey; the PDF
 * prints every storey anyway, so it only narrows PNG, SVG and DXF.
 */

export interface PlanExportStorey {
  readonly story: number;
  readonly label: string;
}

type Item =
  | { readonly key: string; readonly label: string; readonly hint: string; readonly kind: "plan"; readonly format: "pdf" | "png" | "svg" | "dxf" }
  | { readonly key: string; readonly label: string; readonly hint: string; readonly kind: "schedule"; readonly format: "pdf" | "csv" };

const ITEMS: readonly Item[] = [
  { key: "plan-pdf", kind: "plan", format: "pdf", label: "Plan — PDF", hint: "Every floor, a sheet each, to scale" },
  { key: "plan-png", kind: "plan", format: "png", label: "Plan — PNG", hint: "Image of the sheet, 200 dpi" },
  { key: "plan-svg", kind: "plan", format: "svg", label: "Plan — SVG", hint: "Vector, for editing or the web" },
  { key: "plan-dxf", kind: "plan", format: "dxf", label: "Plan — DXF (CAD)", hint: "Layered, millimetres — AutoCAD, Revit, DraftSight" },
  { key: "schedule-pdf", kind: "schedule", format: "pdf", label: "Room schedule — PDF", hint: "Areas and volumes per room, with sources" },
  { key: "schedule-csv", kind: "schedule", format: "csv", label: "Room schedule — CSV", hint: "The same table, for a spreadsheet" },
];

function messageOf(error: unknown): string {
  if (error instanceof ApiUnreachableError) return "The API did not answer. Nothing was downloaded — try again.";
  if (error instanceof ApiError) {
    if (error.status === 403) return `Not available: ${error.message}`;
    if (error.code === "plan_empty") return "Nothing has been scanned or drawn on that floor yet.";
    if (error.status === 503) return "The export engine is not available on this server. The SVG and DXF still work.";
    return error.message;
  }
  return "The export failed.";
}

export function PlanExportMenu({
  surveyId,
  storeys,
}: {
  readonly surveyId: string;
  readonly storeys: readonly PlanExportStorey[];
}): ReactNode {
  const { client } = usePortal();
  const { submit, busy, error } = useSubmit();
  const [open, setOpen] = useState(false);
  const [storey, setStorey] = useState<number | undefined>(storeys[0]?.story);
  const [paper, setPaper] = useState<PlanPaperSize>("a3");
  // Comments are internal notes: their pins go on a file only when asked.
  const [commentPins, setCommentPins] = useState(false);
  const [fetching, setFetching] = useState<string | null>(null);
  const menuId = useId();
  const root = useRef<HTMLDivElement>(null);

  // Keep the chosen storey valid as the scan changes under it.
  useEffect(() => {
    if (storey === undefined || !storeys.some((s) => s.story === storey)) setStorey(storeys[0]?.story);
  }, [storeys, storey]);

  // Close on a click outside or Escape, as a menu does.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const download = (item: Item) =>
    submit(async () => {
      setFetching(item.key);
      try {
        const file: DownloadedFile =
          item.kind === "schedule"
            ? await client.roomSchedule(surveyId, item.format)
            : await client.planExport(surveyId, item.format, {
                // The PDF prints every floor; the single-sheet formats need one.
                ...(item.format !== "pdf" && storey !== undefined ? { storey } : {}),
                paper,
                ...(commentPins ? { commentPins: true } : {}),
              });
        const a = document.createElement("a");
        a.href = file.url;
        a.download = file.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Long enough for the browser to take the bytes; then let the blob go.
        setTimeout(() => URL.revokeObjectURL(file.url), 60_000);
      } finally {
        setFetching(null);
      }
    });

  return (
    <div className="plan-export" ref={root}>
      <button
        type="button"
        className="plan-export__trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        Export <span aria-hidden="true">▾</span>
      </button>
      {open ? (
        <div className="plan-export__panel" id={menuId} role="group" aria-label="Export the plan">
          <div className="plan-export__options">
            {storeys.length > 1 ? (
              <label>
                Floor
                <select value={storey ?? ""} onChange={(e) => setStorey(Number(e.target.value))}>
                  {storeys.map((s) => (
                    <option key={s.story} value={s.story}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label>
              Paper
              <select value={paper} onChange={(e) => setPaper(e.target.value as PlanPaperSize)}>
                <option value="a3">A3</option>
                <option value="a4">A4</option>
              </select>
            </label>
            <label title="Open comments' numbered pins on the plan files — internal notes, so off unless you tick it">
              <input type="checkbox" checked={commentPins} onChange={(e) => setCommentPins(e.target.checked)} />{" "}
              Comment pins
            </label>
          </div>
          <ul className="plan-export__list">
            {ITEMS.map((item) => (
              <li key={item.key}>
                <button type="button" className="plan-export__item" disabled={busy} onClick={() => void download(item)}>
                  <span className="plan-export__label">{fetching === item.key ? `${item.label} — preparing…` : item.label}</span>
                  <span className="plan-export__hint">{item.hint}</span>
                </button>
              </li>
            ))}
          </ul>
          {error !== null ? (
            <p className="plan-export__error" role="alert">
              {messageOf(error)}
            </p>
          ) : null}
          <p className="plan-export__note">
            Drawn from the LiDAR scan — check critical dimensions on site.{" "}
            <HowToLink guide="exports-and-reports">How to use these files</HowToLink>
          </p>
        </div>
      ) : null}
    </div>
  );
}
